import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { doc, updateDoc, onSnapshot, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db, getUserProfile } from '../../utils/firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { signOut } from 'firebase/auth';
import GoogleMapsSatelliteUpload from './GoogleMapsSatelliteUpload';
import SatelliteOverviewMap from './SatelliteOverviewMap';
import PitchSectionConfigModal from '../PitchSectionConfigModal';

const SatelliteManager = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [user, loading, error] = useAuthState(auth);
  
  // Mobile device detection
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  
  // Get API key and log status
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  
  // Feedback modal states
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackForm, setFeedbackForm] = useState({
    from: '',
    topic: '',
    content: '',
    url: ''
  });
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);

  // Feedback topic options
  const feedbackTopics = [
    'Bug Report',
    'Feature Request',
    'User Interface Issue',
    'Performance Issue',
    'Data/Sync Problem',
    'Login/Authentication Issue',
    'Pitch Allocation Issue',
    'Satellite Image Problem',
    'Settings Issue',
    'Setup Wizard Issue',
    'General Feedback',
    'Other'
  ];
  
  // Device detection effect
  useEffect(() => {
    const checkDevice = () => {
      // Check if it's a mobile/tablet device using media queries and user agent
      const isMobileMediaQuery = window.matchMedia('(max-width: 1024px)').matches;
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobileUserAgent = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile|tablet/.test(userAgent);
      
      // Consider it mobile if either condition is true
      setIsMobileDevice(isMobileMediaQuery || isMobileUserAgent);
    };

    checkDevice();
    
    // Listen for window resize to update device detection
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);
  
  useEffect(() => {
    console.log('[SatelliteManager] Component mounted');
    console.log('[SatelliteManager] Google Maps API Key:', googleMapsApiKey);
    console.log('[SatelliteManager] API Key status:', googleMapsApiKey ? 'Present' : 'Missing');
    console.log('[SatelliteManager] API Key length:', googleMapsApiKey?.length);
    console.log('[SatelliteManager] Is Mobile Device:', isMobileDevice);
    
    // Check if GoogleMapsSatelliteUpload is imported correctly
    console.log('[SatelliteManager] GoogleMapsSatelliteUpload component:', typeof GoogleMapsSatelliteUpload);
    
    if (!googleMapsApiKey) {
      console.warn('[SatelliteManager] REACT_APP_GOOGLE_MAPS_API_KEY environment variable is not set');
    }
  }, [googleMapsApiKey, isMobileDevice]);
  
  // State
  const [satelliteConfig, setSatelliteConfig] = useState(null);
  const [clubId, setClubId] = useState(null);
  const [clubName, setClubName] = useState('');
  const [userRole, setUserRole] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [view, setView] = useState(() => {
    // Initialize view from URL or default to overview
    const urlView = searchParams.get('view');
    return urlView || 'overview';
  });
  const [componentError, setComponentError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [recaptureKey, setRecaptureKey] = useState(0); // Force remount on recapture

  // FacilityId handling - get from URL and store in state
  const urlFacilityId = searchParams.get('facilityId');
  const [storedFacilityId, setStoredFacilityId] = useState(null);
  const facilityId = storedFacilityId || urlFacilityId;

  // Store facilityId when it comes from URL
  useEffect(() => {
    if (urlFacilityId && !storedFacilityId) {
      console.log('[SatelliteManager] Storing facilityId from URL:', urlFacilityId);
      setStoredFacilityId(urlFacilityId);
    }
  }, [urlFacilityId, storedFacilityId]);

  // State for section configuration workflow
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [allPitchesToConfigure, setAllPitchesToConfigure] = useState([]);
  const [currentPitchIndex, setCurrentPitchIndex] = useState(0);
  const [currentPitchForConfig, setCurrentPitchForConfig] = useState(null);
  const [collectedSectionConfigs, setCollectedSectionConfigs] = useState([]);
  const [isConfiguringSections, setIsConfiguringSections] = useState(false);

  // Debug logging for view changes
  useEffect(() => {
    console.log('[SatelliteManager] View changed to:', view);
    console.log('[SatelliteManager] Satellite config:', satelliteConfig);
    console.log('[SatelliteManager] Has image URL:', !!satelliteConfig?.imageUrl);
  }, [view, satelliteConfig]);

  // Feedback modal functions
  const openFeedbackModal = () => {
    setFeedbackForm({
      from: user?.email || '',
      topic: '',
      content: '',
      url: window.location.href
    });
    setShowFeedbackModal(true);
  };

  const closeFeedbackModal = () => {
    setShowFeedbackModal(false);
    setFeedbackForm({
      from: '',
      topic: '',
      content: '',
      url: ''
    });
  };

  const handleFeedbackInputChange = (field, value) => {
    setFeedbackForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const submitFeedback = async () => {
    if (!feedbackForm.from || !feedbackForm.topic || !feedbackForm.content) {
      alert('Please fill in all fields');
      return;
    }

    setIsSubmittingFeedback(true);

    try {
      console.log('Submitting feedback:', {
        email: feedbackForm.from,
        club: clubName,
        topic: feedbackForm.topic,
        message: feedbackForm.content
      });

      const formData = new FormData();
      formData.append('form-name', 'feedback-form');
      formData.append('email', feedbackForm.from);
      formData.append('club', clubName);
      formData.append('topic', feedbackForm.topic);
      formData.append('message', feedbackForm.content);
      formData.append('url', feedbackForm.url);
      
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData).toString()
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);

      if (response.ok) {
        alert('✅ Thank you! Your feedback has been sent successfully. We\'ll review it soon.');
        closeFeedbackModal();
      } else {
        console.error('Response not ok:', response.status, response.statusText);
        throw new Error(`Server responded with status: ${response.status}`);
      }
    } catch (error) {
      console.error('Netlify Forms submission failed:', error);
      
      alert('⚠️ Direct submission failed. Opening your email client as backup...');
      
      const subject = `Pitchallo Feedback: ${feedbackForm.topic}`;
      const body = `From: ${feedbackForm.from}
Club: ${clubName}
Topic: ${feedbackForm.topic}
URL: ${feedbackForm.url}
Message:
${feedbackForm.content}

---
Sent from Pitchallo Feedback Form`;

      const mailtoLink = `mailto:giannini000@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      
      try {
        window.location.href = mailtoLink;
        setTimeout(() => {
          alert('Your email client should have opened.');
        }, 1000);
        closeFeedbackModal();
      } catch (mailtoError) {
        console.error('Mailto also failed:', mailtoError);
        closeFeedbackModal();
      }
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      alert('Error logging out. Please try again.');
    }
  };

  // Load user profile and club info
  useEffect(() => {
    const loadUserProfile = async () => {
      if (!user) {
        console.log('[SatelliteManager] No user, returning');
        setIsLoading(false);
        return;
      }

      try {
        console.log('[SatelliteManager] Loading profile for user:', user.uid);
        const profile = await getUserProfile(user.uid);
        
        if (!profile) {
          console.error('[SatelliteManager] No profile found');
          navigate('/login');
          return;
        }

        console.log('[SatelliteManager] Profile loaded:', profile);
        setClubId(profile.clubId);
        setClubName(profile.clubName);
        setUserRole(profile.role);
        setIsInitialized(true);
      } catch (error) {
        console.error('[SatelliteManager] Error loading profile:', error);
        alert('Error loading profile');
      } finally {
        setIsLoading(false);
      }
    };

    loadUserProfile();
  }, [user, navigate]);

  // Listen to satellite config changes
  useEffect(() => {
    if (!clubId || !isInitialized) return;

    // Determine which document to listen to based on facilityId
    const docRef = facilityId 
      ? doc(db, 'clubs', clubId, 'facilities', facilityId)
      : doc(db, 'clubs', clubId);
    
    console.log('[SatelliteManager] Setting up snapshot listener for:', facilityId ? `facility ${facilityId}` : `club ${clubId}`);
    
    const unsubscribe = onSnapshot(
      docRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          console.log('[SatelliteManager] Document data received:', data);
          
          // Build satelliteConfig from flat structure
          const config = {
            imageUrl: data.satelliteImageUrl || data.satelliteImage || null,
            imageWidth: data.imageWidth || 2048,
            imageHeight: data.imageHeight || 1536,
            pitches: data.pitches || []
          };
          
          console.log('[SatelliteManager] Setting satellite config:', config);
          setSatelliteConfig(config);
        } else {
          console.log('[SatelliteManager] Document does not exist');
          setSatelliteConfig({ imageUrl: null, imageWidth: 2048, imageHeight: 1536, pitchBoundaries: [] });
        }
      },
      (error) => {
        console.error('[SatelliteManager] Error in snapshot listener:', error);
      }
    );

    return () => {
      console.log('[SatelliteManager] Cleaning up snapshot listener');
      unsubscribe();
    };
  }, [clubId, facilityId, isInitialized]);

  // State for edit mode (preserve existing pitches)
  const [isEditMode, setIsEditMode] = useState(false);

  // Handle view changes
  const handleViewChange = (newView, editMode = false) => {
    console.log('[SatelliteManager] Changing view to:', newView, 'editMode:', editMode);
    
    if (newView === 'upload') {
      setRecaptureKey(prev => prev + 1);
      if (!editMode) {
        // Recapture mode - clear everything for fresh start
        setSatelliteConfig(null);
        setIsEditMode(false);
      } else {
        // Edit mode - preserve existing config
        setIsEditMode(true);
      }
    }
    setView(newView);
    setSearchParams({ view: newView });
  };

  // Handle edit pitches (preserve existing data)
  const handleEditPitches = () => {
    console.log('[SatelliteManager] Editing pitches - preserving existing data');
    handleViewChange('upload', true);
  };

  // Handle capture complete
  const handleCaptureComplete = (data) => {
    console.log('[SatelliteManager] Capture complete:', data);
    
    // Start the section configuration workflow
    if (data.pitches && data.pitches.length > 0) {
      const pitchesToConfigure = data.pitches.map(pitch => ({
        pitchId: pitch.id,
        pitchName: pitch.name
      }));
      
      setAllPitchesToConfigure(pitchesToConfigure);
      setCurrentPitchIndex(0);
      setCurrentPitchForConfig(pitchesToConfigure[0]);
      setCollectedSectionConfigs([]);
      setIsConfiguringSections(true);
      setShowSectionModal(true);
    } else {
      // No pitches to configure, just switch to overview
      handleViewChange('overview');
    }
  };

  // Handle section configuration workflow
  const handleSectionModalClose = () => {
    setShowSectionModal(false);
    setIsConfiguringSections(false);
    setAllPitchesToConfigure([]);
    setCurrentPitchIndex(0);
    setCurrentPitchForConfig(null);
    setCollectedSectionConfigs([]);
  };

  const handleSectionConfigSave = async (config) => {
    console.log('[SatelliteManager] Section config saved:', config);
    
    // Add to collected configs
    const newConfigs = [...collectedSectionConfigs, {
      pitchId: currentPitchForConfig.pitchId,
      pitchName: currentPitchForConfig.pitchName,
      config: config
    }];
    
    setCollectedSectionConfigs(newConfigs);
    
    // Check if we're done with all pitches
    if (currentPitchIndex + 1 >= allPitchesToConfigure.length) {
      // All pitches configured, save to database
      await saveSectionConfigurations(newConfigs);
      handleSectionModalClose();
      handleViewChange('overview');
    }
  };

  const handleAdvanceToNext = () => {
    const nextIndex = currentPitchIndex + 1;
    if (nextIndex < allPitchesToConfigure.length) {
      setCurrentPitchIndex(nextIndex);
      setCurrentPitchForConfig(allPitchesToConfigure[nextIndex]);
    }
  };


  // ✅ FIXED: Save section configurations to satelliteConfig.pitchBoundaries
  const saveSectionConfigurations = async (configs) => {
    try {
      console.log('[SatelliteManager] Saving section configurations:', configs);
      console.log('[SatelliteManager] Number of configs:', configs.length);
      console.log('[SatelliteManager] Raw configs:', JSON.stringify(configs, null, 2));
      console.log('[SatelliteManager] FacilityId:', facilityId);
      
      // Helper to remove undefined values
      const removeUndefined = (obj) => {
        if (obj === null) return null;
        if (Array.isArray(obj)) {
          return obj.map(item => removeUndefined(item)).filter(item => item !== undefined);
        }
        if (typeof obj === 'object') {
          const cleaned = {};
          for (const [key, value] of Object.entries(obj)) {
            if (value !== undefined) {
              cleaned[key] = removeUndefined(value);
            }
          }
          return cleaned;
        }
        return obj;
      };
      
      // FIXED: Use facility document when facilityId exists
      if (facilityId) {
        console.log('[SatelliteManager] Saving to FACILITY document:', facilityId);
        const facilityRef = doc(db, 'clubs', clubId, 'facilities', facilityId);
        const facilityDoc = await getDoc(facilityRef);
        
        if (!facilityDoc.exists()) {
          throw new Error('Facility document not found');
        }
        
        const facilityData = facilityDoc.data();
        console.log('[SatelliteManager] Facility pitches:', facilityData.pitches);
        
        if (!facilityData.pitches || facilityData.pitches.length === 0) {
          throw new Error('No pitches found in facility');
        }
        
        // Update pitches with section configurations
        const updatedPitches = facilityData.pitches.map(pitch => {
          console.log('[SatelliteManager] Processing facility pitch:', pitch.id);
          
          const configForPitch = configs.find(c => {
            console.log('[SatelliteManager] Comparing config pitchId:', c.pitchId, 'with pitch.id:', pitch.id);
            return c.pitchId === pitch.id;
          });
          
          if (configForPitch) {
            console.log('[SatelliteManager] Found config for pitch:', pitch.id);
            const updatedPitch = { ...pitch };
            
            if (configForPitch.config?.sections !== undefined) {
              updatedPitch.sections = configForPitch.config.sections;
            }
            if (configForPitch.config?.sectionLayout !== undefined) {
              updatedPitch.sectionLayout = configForPitch.config.sectionLayout;
            }
            
            return updatedPitch;
          }
          
          console.log('[SatelliteManager] No config found for pitch:', pitch.id);
          return pitch;
        });
        
        const cleanedPitches = removeUndefined(updatedPitches);
        console.log('[SatelliteManager] Cleaned facility pitches:', JSON.stringify(cleanedPitches, null, 2));
        
        // Save to facility document
        await updateDoc(facilityRef, {
          pitches: cleanedPitches,
          lastUpdated: serverTimestamp(),
          updatedBy: user?.email || 'unknown'
        });
        
        // Also update satelliteConfig.pitches if it exists
        if (facilityData.satelliteConfig) {
          await updateDoc(facilityRef, {
            'satelliteConfig.pitches': cleanedPitches
          });
        }
        
        console.log('[SatelliteManager] Section configs saved to facility:', facilityId);
      } else {
        // Legacy: save to club document
        console.log('[SatelliteManager] Saving to CLUB document (legacy mode)');
        const clubRef = doc(db, 'clubs', clubId);
        const clubDoc = await getDoc(clubRef);
        
        if (!clubDoc.exists()) {
          throw new Error('Club document not found');
        }
        
        const clubData = clubDoc.data();
        console.log('[SatelliteManager] Club data - pitches:', clubData.pitches);
        
        if (!clubData.pitches || clubData.pitches.length === 0) {
          throw new Error('No pitches found');
        }
        
        // Update pitches with section configurations
        const updatedPitches = clubData.pitches.map(pitch => {
          console.log('[SatelliteManager] Processing pitch:', pitch.id);
          
          const configForPitch = configs.find(c => {
            console.log('[SatelliteManager] Comparing config pitchId:', c.pitchId, 'with pitch.id:', pitch.id);
            return c.pitchId === pitch.id;
          });
          
          if (configForPitch) {
            console.log('[SatelliteManager] Found config for pitch:', pitch.id);
            const updatedPitch = { ...pitch };
            
            if (configForPitch.config?.sections !== undefined) {
              updatedPitch.sections = configForPitch.config.sections;
            }
            if (configForPitch.config?.sectionLayout !== undefined) {
              updatedPitch.sectionLayout = configForPitch.config.sectionLayout;
            }
            
            return updatedPitch;
          }
          
          console.log('[SatelliteManager] No config found for pitch:', pitch.id);
          return pitch;
        });
        
        const cleanedPitches = removeUndefined(updatedPitches);
        console.log('[SatelliteManager] Cleaned pitches:', JSON.stringify(cleanedPitches, null, 2));
        
        await updateDoc(clubRef, {
          pitches: cleanedPitches,
          lastUpdated: serverTimestamp(),
          updatedBy: user?.email || 'unknown'
        });
      }
      
      console.log('[SatelliteManager] Section configurations saved successfully');
    } catch (error) {
      console.error('[SatelliteManager] Error saving section configurations:', error);
      console.error('[SatelliteManager] Error details:', error.message);
      alert(`Error saving section configurations: ${error.message}`);
    }
  };

  // Handle save from setup mode
  const handleSaveConfiguration = async (pitches) => {
    try {
      console.log('[SatelliteManager] Saving configuration with pitches:', pitches);
      
      const clubRef = doc(db, 'clubs', clubId);
      await updateDoc(clubRef, {
        pitches: pitches,
        lastUpdated: serverTimestamp(),
        updatedBy: user?.email || 'unknown'
      });
      
      console.log('[SatelliteManager] Configuration saved successfully');
      
      // Start section configuration workflow
      if (pitches && pitches.length > 0) {
        const pitchesToConfigure = pitches.map(pitch => ({
          pitchId: pitch.id,
          pitchName: pitch.name
        }));
        
        setAllPitchesToConfigure(pitchesToConfigure);
        setCurrentPitchIndex(0);
        setCurrentPitchForConfig(pitchesToConfigure[0]);
        setCollectedSectionConfigs([]);
        setIsConfiguringSections(true);
        setShowSectionModal(true);
      } else {
        handleViewChange('overview');
      }
    } catch (error) {
      console.error('[SatelliteManager] Error saving configuration:', error);
      alert('Error saving configuration. Please try again.');
    }
  };

  // Handle pitch click from overview

  // ✅ FIXED: Handle pitch click with proper navigation
  const handlePitchClick = (pitch) => {
    console.log('[SatelliteManager] Pitch clicked:', pitch);
    
    // Extract pitch number from pitch.id (format: "pitch-1", "pitch-2", etc.)
    let pitchNumber;
    if (typeof pitch === 'string') {
      // Old format: "pitch_1" or "pitch-1"
      pitchNumber = pitch.replace('pitch_', '').replace('pitch-', '').replace('pitch', '');
    } else if (pitch.pitchNumber) {
      pitchNumber = pitch.pitchNumber;
    } else if (pitch.id) {
      // Extract from "pitch-1" format
      pitchNumber = pitch.id.replace('pitch-', '').replace('pitch', '');
    } else {
      pitchNumber = 1;
    }
    
    const pitchId = `pitch-${pitchNumber}`;
    console.log('[SatelliteManager] Navigating to:', pitchId);
    navigate(`/allocator/${pitchId}`);
  };

  if (loading || isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#f3f4f6'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #e5e7eb',
            borderTopColor: '#3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <p style={{ color: '#6b7280' }}>Loading...</p>
        </div>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f3f4f6',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Feedback Modal */}
      {showFeedbackModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h2 style={{
              margin: '0 0 20px 0',
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#1f2937'
            }}>
              Send Feedback
            </h2>

            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151'
              }}>
                Your Email *
              </label>
              <input
                type="email"
                value={feedbackForm.from}
                onChange={(e) => handleFeedbackInputChange('from', e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
                placeholder="your@email.com"
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151'
              }}>
                Topic *
              </label>
              <select
                value={feedbackForm.topic}
                onChange={(e) => handleFeedbackInputChange('topic', e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: 'white'
                }}
              >
                <option value="">Select a topic...</option>
                {feedbackTopics.map(topic => (
                  <option key={topic} value={topic}>{topic}</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151'
              }}>
                Message *
              </label>
              <textarea
                value={feedbackForm.content}
                onChange={(e) => handleFeedbackInputChange('content', e.target.value)}
                rows={6}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
                placeholder="Describe your feedback..."
              />
            </div>

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
              marginTop: '24px'
            }}>
              <button
                onClick={closeFeedbackModal}
                disabled={isSubmittingFeedback}
                style={{
                  padding: '10px 20px',
                  backgroundColor: 'white',
                  color: '#4b5563',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: isSubmittingFeedback ? 'not-allowed' : 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={submitFeedback}
                disabled={isSubmittingFeedback}
                style={{
                  padding: '10px 20px',
                  backgroundColor: isSubmittingFeedback ? '#9ca3af' : '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: isSubmittingFeedback ? 'not-allowed' : 'pointer'
                }}
              >
                {isSubmittingFeedback ? 'Sending...' : 'Send Feedback'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{
        backgroundColor: 'white',
        borderBottom: '1px solid #e5e7eb',
        padding: '16px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div>
          <h1 style={{
            margin: 0,
            fontSize: '24px',
            fontWeight: 'bold',
            color: '#1f2937'
          }}>
            Satellite Manager
          </h1>
          <p style={{
            margin: '4px 0 0 0',
            fontSize: '14px',
            color: '#6b7280'
          }}>
            {clubName}
          </p>
        </div>

        <div style={{
          display: 'flex',
          gap: '12px',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={() => navigate('/settings')}
            style={{
              padding: '10px 16px',
              backgroundColor: 'white',
              color: '#4b5563',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            ← Back to Settings
          </button>

          <button
            onClick={() => handleViewChange('upload')}
            title="Redraw all pitches in your facility"
            style={{
              padding: '10px 16px',
              backgroundColor: view === 'upload' ? '#3b82f6' : 'white',
              color: view === 'upload' ? 'white' : '#4b5563',
              border: view === 'upload' ? 'none' : '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Recapture
          </button>

          {/* Edit Pitches - only show when there's existing config */}
          {satelliteConfig?.imageUrl && satelliteConfig?.pitches?.length > 0 && (
            <button
              onClick={handleEditPitches}
              style={{
                padding: '10px 16px',
                backgroundColor: view === 'upload' && isEditMode ? '#10b981' : 'white',
                color: view === 'upload' && isEditMode ? 'white' : '#059669',
                border: view === 'upload' && isEditMode ? 'none' : '1px solid #10b981',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              ✏️ Edit Pitches
            </button>
          )}

          <button
            onClick={openFeedbackModal}
            style={{
              padding: '10px 16px',
              backgroundColor: 'white',
              color: '#4b5563',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            💬 Feedback
          </button>

          <button
            onClick={handleLogout}
            style={{
              padding: '10px 16px',
              backgroundColor: 'rgba(239, 68, 68, 0.2)',
              color: '#dc2626',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            🚪 Logout
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div style={{
        flex: 1,
        padding: '20px',
        overflowY: 'auto'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          
          {/* Check for missing API key */}
          {!googleMapsApiKey && view === 'upload' && (
            <div style={{
              padding: '20px',
              marginBottom: '20px',
              backgroundColor: '#fee2e2',
              border: '1px solid #ef4444',
              borderRadius: '8px'
            }}>
              <h3 style={{ color: '#dc2626', marginTop: 0 }}>Configuration Required</h3>
              <p style={{ color: '#dc2626' }}>
                Google Maps API key is not configured. The satellite capture feature requires a valid API key.
              </p>
              <p style={{ color: '#dc2626', marginBottom: 0 }}>
                Please set the REACT_APP_GOOGLE_MAPS_API_KEY environment variable in Netlify.
              </p>
            </div>
          )}

          {/* Stable rendering instead of conditional rendering */}
          
          {/* View: Upload/Capture satellite image */}
          <div style={{ display: view === 'upload' ? 'block' : 'none' }}>
            {console.log('[SatelliteManager] Rendering GoogleMapsSatelliteUpload with API key:', googleMapsApiKey, 'editMode:', isEditMode)}
            <GoogleMapsSatelliteUpload
              key={`satellite-upload-${recaptureKey}`}
              clubId={clubId}
              clubName={clubName}
              facilityId={facilityId}
              onCaptureComplete={handleCaptureComplete}
              googleMapsApiKey={googleMapsApiKey}
              existingConfig={isEditMode ? satelliteConfig : null}
            />
          </div>

          {/* View: Setup pitch boundaries */}
          <div style={{ display: view === 'setup' && satelliteConfig?.imageUrl ? 'block' : 'none' }}>
            <h2 style={{ marginBottom: '20px', color: '#059669' }}>
              Setup Mode - Draw Pitch Boundaries
            </h2>
            {componentError && (
              <div style={{
                padding: '10px',
                marginBottom: '20px',
                backgroundColor: '#fee2e2',
                border: '1px solid #ef4444',
                borderRadius: '6px',
                color: '#dc2626'
              }}>
                Component Error: {componentError}
              </div>
            )}
            <div style={{
              border: '2px solid #059669',
              borderRadius: '8px',
              padding: '10px',
              backgroundColor: 'white'
            }}>
              <SatelliteOverviewMap
                key="satellite-setup"
                clubId={clubId}
                satelliteConfig={satelliteConfig}
                isSetupMode={true}
                onSaveConfiguration={handleSaveConfiguration}
                googleMapsApiKey={googleMapsApiKey}
                existingPitches={satelliteConfig?.pitches || []}
                isEditingFacility={satelliteConfig?.pitches?.length > 0}
                onError={(error) => {
                  console.error('[SatelliteManager] SatelliteOverviewMap error:', error);
                  setComponentError(error.message || 'Component failed to load');
                }}
              />
            </div>
          </div>

          {/* View: Overview map (navigation mode) */}
          <div style={{ display: view === 'overview' && satelliteConfig?.imageUrl ? 'block' : 'none' }}>
            <h2 style={{ marginBottom: '20px' }}>Overview Mode - Click Pitches to Navigate</h2>
            <SatelliteOverviewMap
              key="satellite-overview"
              clubId={clubId}
              satelliteConfig={satelliteConfig}
              isSetupMode={false}
              onPitchClick={handlePitchClick}
              googleMapsApiKey={googleMapsApiKey}
              onError={(error) => {
                console.error('[SatelliteManager] SatelliteOverviewMap error:', error);
                setComponentError(error.message || 'Component failed to load');
              }}
            />
          </div>

          {/* Fallback if no view matches or image missing */}
          {!satelliteConfig?.imageUrl && view !== 'upload' && (
            <div style={{
              padding: '20px',
              textAlign: 'center',
              backgroundColor: '#fee2e2',
              border: '1px solid #ef4444',
              borderRadius: '8px'
            }}>
              <h3>No Satellite Image</h3>
              <p>Please capture a satellite image first to continue.</p>
              <button
                onClick={() => handleViewChange('upload')}
                style={{
                  marginTop: '10px',
                  padding: '8px 16px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Capture Satellite
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Section configuration modal */}
      <PitchSectionConfigModal
        isOpen={showSectionModal}
        onClose={handleSectionModalClose}
        onSave={handleSectionConfigSave}
        onAdvanceToNext={handleAdvanceToNext}
        pitchId={currentPitchForConfig?.pitchId}
        pitchName={currentPitchForConfig?.pitchName}
        currentConfig={null}
        // Multi-pitch workflow props
        allPitchNames={allPitchesToConfigure.map(pitch => pitch.pitchName)}
        currentPitchIndex={currentPitchIndex}
        totalPitches={allPitchesToConfigure.length}
      />
      
      {/* Configuration progress indicator */}
      {isConfiguringSections && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: 'white',
          padding: '16px',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          border: '2px solid #3b82f6',
          zIndex: 999
        }}>
          <div style={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#1f2937',
            marginBottom: '8px'
          }}>
            Configuring Pitch Sections
          </div>
          <div style={{
            fontSize: '12px',
            color: '#6b7280'
          }}>
            {collectedSectionConfigs.length} of {allPitchesToConfigure.length} completed
          </div>
          <div style={{
            width: '200px',
            height: '4px',
            backgroundColor: '#e5e7eb',
            borderRadius: '2px',
            marginTop: '8px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${(collectedSectionConfigs.length / allPitchesToConfigure.length) * 100}%`,
              height: '100%',
              backgroundColor: '#3b82f6',
              transition: 'width 0.3s ease'
            }}></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SatelliteManager;
